import React from 'react';
import { LandmineItem, getScoreBadgeClasses, getPriorityClasses } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface LandmineSummaryCardProps {
  items: LandmineItem[];
  overallSummary?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const LandmineSummaryCard: React.FC<LandmineSummaryCardProps> = ({
  items,
  overallSummary,
  onViewDetails,
  onViewInTimeline,
}) => {
  const count = items?.length ?? 0;

  const avgScore =
    count > 0
      ? Math.round(items.reduce((sum, item) => sum + (item.handling_score ?? 0), 0) / count)
      : null;

  const highPriorityUnhandled = items.filter(
    (item) =>
      item.priority?.toLowerCase() === 'high' &&
      item.handling_label !== 'well_handled'
  );

  const firstHighPriority = items.find((item) => item.priority?.toLowerCase() === 'high');
  const timelineTs = firstHighPriority?.timestamp_iso ?? items[0]?.timestamp_iso;

  const byRep = items.filter((item) => item.triggered_by?.toLowerCase() === 'salesperson').length;
  const byCustomer = items.filter((item) => item.triggered_by?.toLowerCase() === 'customer').length;

  const badgeColor =
    avgScore == null
      ? 'bg-surface-subtle text-ink-muted border border-hairline'
      : getScoreBadgeClasses(avgScore);

  const statusBadge =
    count === 0 ? (
      <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-success-tint text-success border border-success">Clean</span>
    ) : highPriorityUnhandled.length > 0 ? (
      <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-error-tint text-error border border-error">
        {highPriorityUnhandled.length} HIGH unhandled
      </span>
    ) : avgScore != null ? (
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>Avg {avgScore}%</span>
    ) : undefined;

  const coachingItems: string[] = [];
  if (highPriorityUnhandled.length > 0) {
    coachingItems.push(
      `${highPriorityUnhandled.length} high-priority landmine${highPriorityUnhandled.length !== 1 ? 's' : ''} were not handled well. Review the approved talking points for: ${highPriorityUnhandled.slice(0, 2).map((item) => item.trigger).join(', ')}.`
    );
  }
  if (byRep > 0) {
    coachingItems.push(
      `The salesperson triggered ${byRep} landmine${byRep !== 1 ? 's' : ''} proactively. Review the topics flagged as compliance risks to avoid raising them unprompted.`
    );
  }

  const resultContent = (
    <div className="space-y-2">
      {count === 0 ? (
        <p className="text-xs text-success font-medium">No compliance landmines were triggered in this call.</p>
      ) : (
        <>
          {overallSummary && <p className="text-xs text-ink-secondary">{overallSummary}</p>}
          <div className="flex items-center gap-3 mt-1">
            <div className="text-center">
              <p className="text-xl font-bold text-ink-secondary">{count}</p>
              <p className="text-[10px] text-ink-muted">landmine{count !== 1 ? 's' : ''}</p>
            </div>
            {avgScore != null && (
              <div className="text-center">
                <p className={`text-xl font-bold ${avgScore >= 70 ? 'text-success' : avgScore >= 50 ? 'text-warning' : 'text-error'}`}>
                  {avgScore}%
                </p>
                <p className="text-[10px] text-ink-muted">avg handling</p>
              </div>
            )}
          </div>
          <div className="flex gap-2 text-[11px] mt-1">
            {byRep > 0 && (
              <span className="bg-warning-tint text-warning border border-warning rounded px-2 py-0.5">
                {byRep} by rep
              </span>
            )}
            {byCustomer > 0 && (
              <span className="bg-info-tint text-info border border-info rounded px-2 py-0.5">
                {byCustomer} by customer
              </span>
            )}
          </div>
          {highPriorityUnhandled.length > 0 && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-error uppercase">High Priority — Unhandled</p>
              {highPriorityUnhandled.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between bg-error-tint rounded px-2 py-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${getPriorityClasses(item.priority)}`}>
                      {item.priority?.toUpperCase()}
                    </span>
                    <span className="text-xs text-ink-secondary truncate">{item.trigger}</span>
                  </div>
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ml-2 flex-shrink-0 ${getScoreBadgeClasses(item.handling_score)}`}>
                    {item.handling_score}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Landmine Handling"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingItems.length > 0 ? coachingItems : undefined}
      sectionKey="landmine"
      onViewDetails={onViewDetails}
      onViewInTimeline={timelineTs ? onViewInTimeline : undefined}
    />
  );
};

export default LandmineSummaryCard;
